import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  ProjectTasksDataCollectionDocument,
  UpdateProjectTaskDocument,
  UpdateProjectTaskMutationVariables,
} from 'generated/resources/graphql'
import { UpdateProjectTaskDataDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { projectTaskToFormData } from 'modules/project_management/lib/tasks'

import { ProjectTaskForm } from 'modules/project_management/components/TaskForm/ProjectTaskForm'

export interface Props {
  projectId: string
  taskId: string
  open: boolean
  onClose: () => void
}

interface TaskFile {
  id: string
  url: string
  filename: string
  contentType: string
}

export const TasksSidePanel = ({ projectId, taskId, open, onClose }: Props) => {
  const { data: taskData, loading } = useQuery(UpdateProjectTaskDataDocument, {
    variables: { id: taskId },
    fetchPolicy: 'network-only',
  })

  const projectTask = taskData?.projectManagement.projectTask

  const formData = projectTask ? projectTaskToFormData(projectId, projectTask) : null

  const buildFiles = (files: TaskFile[]) => {
    return files.map(({ filename, contentType }) => {
      return new File([], filename, { type: contentType })
    })
  }

  const [data, setData] = useState<UpdateProjectTaskMutationVariables | null>(formData)
  const initialFiles = buildFiles(projectTask?.task?.attachments ?? [])
  const [filesToRemove, setFilesToRemove] = useState<string[]>([])
  const [filesToAdd, setFilesToAdd] = useState<File[]>([])

  const [updateProjectTask] = useMutation(UpdateProjectTaskDocument, {
    extractorFn: (mutationData) =>
      mutationData.projectManagementMutations.updateProjectmanagementProjectTask,
  })

  useEffect(() => {
    setData(formData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffect(() => {
    track(trackingEvents.projectManagement.tasks.editProjectTask)
  }, [])

  const onChange = (value: UpdateProjectTaskMutationVariables | null) => {
    if (value) {
      setData(value)
    }
  }

  const onFilesChange = (formFiles: File[]) => {
    const idsToRemove =
      projectTask?.task?.attachments
        .filter((file) => {
          return !formFiles.find((formFile) => formFile.name === file.filename)
        })
        .map((file) => file.id) || []

    const newFiles = formFiles.filter((formFile) => {
      return !projectTask?.task?.attachments.find((file) => file.filename === formFile.name)
    })
    setFilesToAdd(newFiles)
    setFilesToRemove(idsToRemove)
  }

  const onSubmit = async () => {
    track(trackingEvents.projectManagement.tasks.submitEditProjectTask)
    if (data) {
      const result = await updateProjectTask({
        variables: { ...data, followUp: data.followUp ?? false, filesToRemove, filesToAdd },
        awaitRefetchQueries: true,
        refetchQueries: [ProjectTasksDataCollectionDocument],
      })

      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('project_management.tasks.edit_task.success_message'),
          time: 2000,
        })
        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('project_management.tasks.edit_task.error_message'),
          time: 2000,
        })
      }
    }
  }
  const taskName = projectTask?.task?.name || ''
  return (
    <Sidepanel
      width="wide"
      title={i18n.t('project_management.tasks.edit_task.edit_title', { task_name: taskName })}
      open={open}
      onClose={onClose}
      closeLabel={i18n.t('common.close')}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} type="primary">
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      {loading && (
        <Box height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )}
      {data && (
        <ProjectTaskForm<UpdateProjectTaskMutationVariables>
          task={data}
          initialFiles={initialFiles}
          onChange={onChange}
          onFilesChange={onFilesChange}
        />
      )}
    </Sidepanel>
  )
}
